import { useEffect, useState, useCallback, useMemo } from "react";
import { api, apiError } from "@/lib/api";
import { PageHeader, StatusBadge, fmtDate } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Radar, ExternalLink, Clock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { FleetMap } from "@/components/routing/FleetMap";
import { HEALTH } from "@/components/routing/TrackingPills";

const today = () => new Date().toISOString().slice(0, 10);
const ago = (s) => (s == null ? "never" : s < 60 ? `${s}s ago` : `${Math.round(s / 60)} min ago`);

const Breadcrumbs = ({ sessionId }) => {
  const [pts, setPts] = useState(null);
  useEffect(() => { api.get(`/tracking/sessions/${sessionId}/locations`).then((r) => setPts(r.data)).catch(() => setPts([])); }, [sessionId]);
  if (!pts) return <Loader2 className="w-4 h-4 animate-spin" />;
  if (!pts.length) return <p className="text-xs text-muted-foreground">No location points received yet.</p>;
  return (
    <div className="max-h-56 overflow-y-auto text-xs border border-border rounded-md mt-2" data-testid="breadcrumbs">
      {pts.slice(-50).reverse().map((p) => (
        <div key={p.id} className="flex justify-between px-3 py-1.5 border-b border-border last:border-0">
          <span>{p.lat.toFixed(5)}, {p.lng.toFixed(5)}{p.accuracy ? ` ±${Math.round(p.accuracy)}m` : ""}</span>
          <span className="text-muted-foreground">{fmtDate(p.received_at)}</span>
        </div>
      ))}
    </div>
  );
};

export default function LiveTracking() {
  const [live, setLive] = useState(null);
  const [open, setOpen] = useState(null);
  const [day, setDay] = useState(today());
  const [history, setHistory] = useState([]);
  const load = useCallback(() => api.get("/tracking/live").then((r) => setLive(r.data)).catch((e) => { toast.error(apiError(e)); setLive([]); }), []);
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);
  useEffect(() => { api.get("/work-sessions", { params: { date: day } }).then((r) => setHistory(r.data)).catch(() => setHistory([])); }, [day, live]);
  const markers = useMemo(() => (live || []).filter((s) => s.last_location).map((s) => ({ id: s.id, lat: s.last_location.lat, lng: s.last_location.lng, label: s.driver_name?.[0], title: s.driver_name, color: s.stale ? "#9ca3af" : "#C8202F" })), [live]);

  return (
    <div data-testid="live-tracking-page">
      <PageHeader title="Live Driver Tracking" subtitle="Real device locations reported by on-duty drivers. Refreshes every 15 seconds."
        actions={<span className="inline-flex items-center gap-1.5 rounded-full bg-success/15 text-success text-xs font-bold px-3 py-1.5"><Radar className="w-3.5 h-3.5" /> LIVE — DRIVER DEVICE GPS</span>} />
      <p className="text-xs text-muted-foreground mb-4">Locations are only collected while a driver is clocked in and has allowed location sharing. Points older than 5 minutes are marked stale.</p>
      <div className="mb-6">
        <FleetMap markers={markers} />
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4 mb-8">
        {live === null && <Loader2 className="w-5 h-5 animate-spin text-primary" />}
        {live?.length === 0 && <p className="text-sm text-muted-foreground" data-testid="no-live-drivers">No drivers are clocked in right now.</p>}
        {live?.map((s) => {
          const l = s.last_location;
          return (
            <div key={s.id} className="border border-border rounded-lg bg-card p-4" data-testid="live-driver-card">
              <div className="flex items-center justify-between"><span className="font-semibold">{s.driver_name}</span><StatusBadge value={s.duty_status === "ON_BREAK" ? "ON_BREAK" : "WORKING"} /></div>
              <p className="text-xs mt-1" data-testid="tracking-health">Tracking: <b className={s.tracking_health === "LIVE" ? "text-success" : "text-danger"}>{HEALTH[s.tracking_health]?.[0] || s.tracking_health}</b></p>
              <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1"><Clock className="w-3 h-3" /> On duty since {fmtDate(s.clock_in_at)}</p>
              {l ? (
                <div className="mt-3 text-sm">
                  <p className="font-mono">{l.lat.toFixed(5)}, {l.lng.toFixed(5)}</p>
                  <p className="text-xs text-muted-foreground">Updated {ago(s.seconds_since_update)}{l.accuracy ? ` · ±${Math.round(l.accuracy)}m` : ""}{l.trip_id ? " · on trip" : ""}</p>
                  <a className="inline-flex items-center gap-1 text-xs text-brand mt-1" href={`https://www.google.com/maps?q=${l.lat},${l.lng}`} target="_blank" rel="noreferrer" data-testid="open-in-maps">Open in Maps <ExternalLink className="w-3 h-3" /></a>
                </div>
              ) : <p className="mt-3 text-xs text-muted-foreground">No location shared yet.</p>}
              <Button size="sm" variant="ghost" className="mt-2 px-0" onClick={() => setOpen(open === s.id ? null : s.id)} data-testid="toggle-breadcrumbs">{open === s.id ? "Hide" : "Show"} location history</Button>
              {open === s.id && <Breadcrumbs sessionId={s.id} />}
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-between mb-3"><h2 className="font-heading font-semibold">Work Sessions</h2><Input type="date" value={day} onChange={(e) => setDay(e.target.value)} className="w-44" data-testid="sessions-date" /></div>
      <div className="border border-border rounded-lg bg-card divide-y divide-border" data-testid="work-sessions-list">
        {history.length === 0 && <p className="p-4 text-sm text-muted-foreground">No sessions on this date.</p>}
        {history.map((s) => (
          <div key={s.id} className="p-3 flex flex-wrap gap-4 text-sm items-center" data-testid="work-session-row">
            <span className="font-semibold min-w-[140px]">{s.driver_name}</span>
            <span>In: {fmtDate(s.clock_in_at)}</span>
            <span>Out: {s.clock_out_at ? fmtDate(s.clock_out_at) : "—"}</span>
            <span>{s.duration_minutes != null ? `${s.duration_minutes} min` : "in progress"}</span>
            <span className="text-muted-foreground">{(s.breaks || []).length} break(s)</span>
            <StatusBadge value={s.status} />
          </div>
        ))}
      </div>
    </div>
  );
}
